import React, { useMemo, useState } from 'react';
import { Card, Input, InputNumber, Button, Toast, Modal, Steps } from '@douyinfe/semi-ui';
import { IconMoneyExchangeStroked, IconRefresh, IconTickCircle } from '@douyinfe/semi-icons';
import axios from 'axios';

const BalanceTransfer = () => {
    const [fromKey, setFromKey] = useState('');
    const [toKey, setToKey] = useState('');
    const [amount, setAmount] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fromTokenInfo, setFromTokenInfo] = useState(null);
    const [toTokenInfo, setToTokenInfo] = useState(null);
    const [queryingFrom, setQueryingFrom] = useState(false);
    const [queryingTo, setQueryingTo] = useState(false);

    const API_BASE = process.env.REACT_APP_TRANSFER_API_URL || 'https://tr.wyzlab.ai';

    // 计算当前步骤
    const currentStep = useMemo(() => {
        if (!fromTokenInfo) return 0;
        if (!toTokenInfo) return 1;
        if (!amount || amount <= 0) return 2;
        return 3;
    }, [fromTokenInfo, toTokenInfo, amount]);

    const EXCHANGE_RATE = 500000; // 500,000 quota = $1

    const sanitizeKey = (key) => key.trim().replace(/^sk-/, '');

    const balanceToUSD = (balance) => {
        const numeric = Number(balance);
        if (Number.isNaN(numeric)) return 0;
        return numeric / EXCHANGE_RATE;
    };

    const usdToBalance = (usd) => {
        const numeric = Number(usd);
        if (Number.isNaN(numeric)) return 0;
        return Math.round(numeric * EXCHANGE_RATE);
    };

    const formatUSD = (value, placeholder = '--') => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return placeholder;
        }
        const numeric = Number(value);
        return `$${numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const fromTokenUSD = useMemo(
        () => (fromTokenInfo ? balanceToUSD(fromTokenInfo.remain_quota) : null),
        [fromTokenInfo]
    );
    const toTokenUSD = useMemo(
        () => (toTokenInfo ? balanceToUSD(toTokenInfo.remain_quota) : null),
        [toTokenInfo]
    );

    const amountUSD = typeof amount === 'number' ? amount : 0;
    const hasValidAmount = typeof amount === 'number' && amount > 0;

    const queryTokenInfo = async (rawKey, isFromToken = true) => {
        const trimmedKey = rawKey.trim();
        if (!trimmedKey || trimmedKey.length < 10) {
            Toast.warning('请输入完整的令牌 key');
            return;
        }

        const setQueryLoading = isFromToken ? setQueryingFrom : setQueryingTo;
        const setTokenInfo = isFromToken ? setFromTokenInfo : setToTokenInfo;

        setQueryLoading(true);
        try {
            const cleanKey = sanitizeKey(trimmedKey);
            const response = await axios.get(`${API_BASE}/api/token/${cleanKey}`);

            if (response.data.success) {
                setTokenInfo(response.data.data);
                Toast.success(`查询成功：${response.data.data.name}`);
            } else {
                Toast.error('查询失败');
                setTokenInfo(null);
            }
        } catch (error) {
            console.error('查询令牌信息失败:', error);
            Toast.error(error.response?.data?.detail || '查询失败，请检查令牌是否正确');
            setTokenInfo(null);
        } finally {
            setQueryLoading(false);
        }
    };

    const handleTransfer = () => {
        if (!fromKey || !toKey) {
            Toast.warning('请输入源令牌和目标令牌');
            return;
        }

        if (!hasValidAmount) {
            Toast.warning('请输入有效的转移金额（USD）');
            return;
        }

        if (!fromTokenInfo || !toTokenInfo) {
            Toast.warning('请先查询令牌信息');
            return;
        }

        if (fromTokenUSD !== null && amountUSD > fromTokenUSD) {
            Toast.error(`转移金额不能超过源令牌余额 (${formatUSD(fromTokenUSD)})`);
            return;
        }

        Modal.confirm({
            title: '确认转移金额',
            content: (
                <div className="transfer-confirm">
                    <div className="transfer-confirm__row">
                        <span>源令牌</span>
                        <span>{fromTokenInfo.name}</span>
                    </div>
                    <div className="transfer-confirm__row">
                        <span>目标令牌</span>
                        <span>{toTokenInfo.name}</span>
                    </div>
                    <div className="transfer-confirm__divider" />
                    <div className="transfer-confirm__row transfer-confirm__row--strong">
                        <span>转移金额</span>
                        <span>{formatUSD(amountUSD)}</span>
                    </div>
                    <div className="transfer-confirm__sub">
                        <span>转移后源令牌余额</span>
                        <span>{formatUSD(Math.max((fromTokenUSD || 0) - amountUSD, 0))}</span>
                    </div>
                    <div className="transfer-confirm__sub">
                        <span>转移后目标令牌余额</span>
                        <span>{formatUSD((toTokenUSD || 0) + amountUSD)}</span>
                    </div>
                </div>
            ),
            okText: '确认转移',
            cancelText: '取消',
            onOk: () => executeTransfer(amountUSD)
        });
    };

    const executeTransfer = async (amountUSDValue) => {
        setLoading(true);
        try {
            const transferQuota = usdToBalance(amountUSDValue);
            if (transferQuota <= 0) {
                Toast.error('转移金额必须大于 0');
                setLoading(false);
                return;
            }

            const cleanFromKey = sanitizeKey(fromKey);
            const cleanToKey = sanitizeKey(toKey);

            const response = await axios.post(
                `${API_BASE}/api/transfer`,
                {
                    from_key: cleanFromKey,
                    to_key: cleanToKey,
                    amount: transferQuota
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                Toast.success(`${formatUSD(amountUSDValue)} 转移成功`);

                setFromTokenInfo((prev) => (prev ? { ...prev, remain_quota: response.data.from_token.remain_quota } : prev));
                setToTokenInfo((prev) => (prev ? { ...prev, remain_quota: response.data.to_token.remain_quota } : prev));
                setAmount(null);
            } else {
                Toast.error('转移失败');
            }
        } catch (error) {
            console.error('转移失败:', error);
            Toast.error(error.response?.data?.detail || '转移失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFromKey('');
        setToKey('');
        setAmount(null);
        setFromTokenInfo(null);
        setToTokenInfo(null);
    };

    const TokenCard = ({
        title,
        hint,
        keyValue,
        onKeyChange,
        onQuery,
        tokenInfo,
        balanceUSD,
        loading,
        querying
    }) => {
        const trimmedValue = keyValue.trim();
        const hasInfo = Boolean(tokenInfo);
        const name = hasInfo ? tokenInfo.name || '--' : '--';
        const group = hasInfo ? tokenInfo.group || '默认' : '--';
        const balanceText = hasInfo ? formatUSD(balanceUSD) : '--';

        return (
            <section className="token-card">
                <header className="token-card__header">
                    <div className="token-card__header-text">
                        <span className="token-card__title">{title}</span>
                        <p className="token-card__hint">{hint}</p>
                    </div>
                    <span className={`token-card__balance${hasInfo ? '' : ' token-card__balance--empty'}`}>{balanceText}</span>
                </header>
                <div className="token-card__controls">
                    <Input
                        className="token-card__input"
                        placeholder="请输入令牌 key（可含 sk- 前缀）"
                        value={keyValue}
                        onChange={onKeyChange}
                        onEnterPress={() => onQuery(keyValue)}
                        allowClear
                        disabled={loading}
                    />
                    <Button
                        className="token-card__btn"
                        icon={<IconRefresh />}
                        onClick={() => onQuery(keyValue)}
                        loading={querying}
                        disabled={!trimmedValue || loading}
                    >
                        查询
                    </Button>
                </div>
                <div className="token-card__meta">
                    <div className="token-card__meta-row">
                        <span>名称</span>
                        <span>{name}</span>
                    </div>
                    <div className="token-card__meta-row">
                        <span>分组</span>
                        <span className="token-card__tag">{group}</span>
                    </div>
                    <div className="token-card__meta-row token-card__meta-row--highlight">
                        <span>当前余额</span>
                        <span>{balanceText}</span>
                    </div>
                </div>
            </section>
        );
    };

    return (
        <div className="transfer-page">
            {/* 页面标题 */}
            <section className="transfer-hero">
                <h1 className="transfer-hero-title">余额转移</h1>
                <p className="transfer-hero-subtitle">安全地在不同令牌之间转移额度</p>
            </section>

            {/* 步骤指示器 */}
            <section className="transfer-steps-section">
                <Card className="glass-card steps-card">
                    <Steps current={currentStep} type="basic" size="small">
                        <Steps.Step 
                            title="选择源令牌" 
                            description="输入并查询转出令牌"
                            icon={fromTokenInfo ? <IconTickCircle /> : null}
                        />
                        <Steps.Step 
                            title="选择目标令牌" 
                            description="输入并查询转入令牌"
                            icon={toTokenInfo ? <IconTickCircle /> : null}
                        />
                        <Steps.Step 
                            title="设置转移金额" 
                            description="输入要转移的金额"
                            icon={hasValidAmount ? <IconTickCircle /> : null}
                        />
                        <Steps.Step 
                            title="确认并转移" 
                            description="核对信息后执行"
                        />
                    </Steps>
                </Card>
            </section>

            {/* 令牌卡片 */}
            <div className="transfer-tokens-grid">
                <section className="transfer-token-section">
                    <div className="token-section-header">
                        <h3 className="token-section-title">1. 源令牌（转出）</h3>
                        <span className={`token-status ${fromTokenInfo ? 'token-status--active' : ''}`}>
                            {fromTokenInfo ? '✓ 已查询' : '待查询'}
                        </span>
                    </div>
                    <TokenCard
                        title="源令牌"
                        hint="输入源令牌 key 并点击查询"
                        keyValue={fromKey}
                        onKeyChange={setFromKey}
                        onQuery={(value) => queryTokenInfo(value, true)}
                        tokenInfo={fromTokenInfo}
                        balanceUSD={fromTokenUSD}
                        loading={loading}
                        querying={queryingFrom}
                    />
                </section>

                <section className="transfer-token-section">
                    <div className="token-section-header">
                        <h3 className="token-section-title">2. 目标令牌（转入）</h3>
                        <span className={`token-status ${toTokenInfo ? 'token-status--active' : ''}`}>
                            {toTokenInfo ? '✓ 已查询' : '待查询'}
                        </span>
                    </div>
                    <TokenCard
                        title="目标令牌"
                        hint="输入目标令牌 key 并点击查询"
                        keyValue={toKey}
                        onKeyChange={setToKey}
                        onQuery={(value) => queryTokenInfo(value, false)}
                        tokenInfo={toTokenInfo}
                        balanceUSD={toTokenUSD}
                        loading={loading}
                        querying={queryingTo}
                    />
                </section>
            </div>

            {/* 转移操作区 */}
            <section className="transfer-action-section">
                <Card className="glass-card transfer-action-card">
                    <div className="transfer-action-header">
                        <div>
                            <h3 className="transfer-action-title">3. 设置转移金额</h3>
                            <p className="transfer-action-desc">请输入要转移的金额（USD）</p>
                        </div>
                        <span className="transfer-available">
                            可用余额 {formatUSD(fromTokenInfo ? fromTokenUSD : null)}
                        </span>
                    </div>

                    <div className="transfer-input-wrapper">
                        <InputNumber
                            className="transfer-amount-input-field"
                            value={amount}
                            onChange={(value) => setAmount(typeof value === 'number' ? value : null)}
                            min={0}
                            step={0.01}
                            precision={2}
                            placeholder="0.00"
                            disabled={loading || !fromTokenInfo || !toTokenInfo}
                            max={fromTokenUSD !== null ? fromTokenUSD : undefined}
                            size="large"
                            prefix="$"
                        />
                        <span className="transfer-rate-hint">1 USD ≈ 500,000 tokens</span>
                    </div>

                    {hasValidAmount && (fromTokenInfo || toTokenInfo) && (
                        <div className="transfer-preview-section">
                            <h4 className="transfer-preview-title">转移预览</h4>
                            <div className="transfer-preview-cards">
                                {fromTokenInfo && (
                                    <div className="transfer-preview-item">
                                        <div className="preview-item-label">源令牌余额变化</div>
                                        <div className="preview-item-change">
                                            <span className="preview-old">{formatUSD(fromTokenUSD)}</span>
                                            <span className="preview-arrow">→</span>
                                            <span className="preview-new">{formatUSD(Math.max((fromTokenUSD || 0) - amountUSD, 0))}</span>
                                        </div>
                                    </div>
                                )}
                                {toTokenInfo && (
                                    <div className="transfer-preview-item">
                                        <div className="preview-item-label">目标令牌余额变化</div>
                                        <div className="preview-item-change">
                                            <span className="preview-old">{formatUSD(toTokenUSD)}</span>
                                            <span className="preview-arrow">→</span>
                                            <span className="preview-new preview-new--increase">{formatUSD((toTokenUSD || 0) + amountUSD)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="transfer-action-buttons">
                        <Button 
                            size="large" 
                            onClick={handleReset} 
                            disabled={loading}
                        >
                            重置
                        </Button>
                        <Button
                            className="transfer-submit-btn"
                            size="large"
                            type='primary'
                            theme="solid"
                            icon={<IconMoneyExchangeStroked />}
                            onClick={handleTransfer}
                            loading={loading}
                            disabled={!fromTokenInfo || !toTokenInfo || !hasValidAmount}
                        >
                            确认转移
                        </Button>
                    </div>
                </Card>
            </section>
        </div>
    );
};

export default BalanceTransfer;
